import React, { useState, useEffect, memo, useRef } from 'react';
import {
  User,
  Phone,
  CalendarDays,
  Users,
  ChevronDown,
  ChevronUp,
  Clock,
} from 'lucide-react';

import ScanBadgeButton from '../../components/scan-badge-button/scan-badge-button';
import { hardwareList } from '../../assets/hardwareList';
import { fetchUserRecord } from '../../utils/mongoDataOperations';
import { formatDate, calculateAge } from '../../utils/helpers';

// Memoized clock component to prevent unnecessary re-renders
const LiveClock = memo(() => {
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    // Update every second
    const timer = setInterval(() => {
      setTime(new Date());
    }, 1000);

    // Cleanup interval on unmount
    return () => clearInterval(timer);
  }, []);

  return (
    <div className='flex items-center mb-4 space-x-2 text-gray-600'>
      <Clock className='h-5 w-5' />
      <span className='text-lg'>
        {time.toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        })}
      </span>
    </div>
  );
});

LiveClock.displayName = 'LiveClock';

const HardwarePage = ({ user }) => {
  const [activeTab, setActiveTab] = useState('checkout');
  const [scannedReturnedID, setScannedReturnedID] = useState(null);
  const [scannedUserRecord, setScannedUserRecord] = useState(null);
  const [checkInDisabled, setCheckInDisabled] = useState(true);
  const [actionSuccess, setActionSuccess] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [selectedItems, setSelectedItems] = useState({});
  const [otherItems, setOtherItems] = useState('');
  const textAreaRef = useRef(null);

  // State to track quantities for selected items
  const [itemQuantities, setItemQuantities] = useState({});

  // Process the hardware list into a global state
  const processHardwareList = () => {
    const items = { ...selectedItems };

    // Process checked items
    Object.entries(itemQuantities).forEach(([item, quantity]) => {
      if (selectedItems[item]) {
        items[item] = parseInt(quantity) || 1;
      }
    });

    // Process other items
    if (otherItems.trim()) {
      if (otherItems.trim().includes(',')) {
        otherItems.split('\n').forEach((line) => {
          const [item, quantity] = line.split(',').map((str) => str.trim());
          if (item && quantity) {
            items[item] = parseInt(quantity) || 1;
          }
        });
      }
    }

    return items;
  };

  // Handle checkbox changes
  const handleCheckboxChange = (itemName) => {
    setSelectedItems((prev) => ({
      ...prev,
      [itemName]: !prev[itemName],
    }));
  };

  // Handle quantity changes
  const handleQuantityChange = (itemName, value) => {
    setItemQuantities((prev) => ({
      ...prev,
      [itemName]: value,
    }));
  };

  // Handle text area changes with focus preservation
  const handleTextAreaChange = (e) => {
    setOtherItems(e.target.value);
    // Ensure the text area keeps focus
    if (textAreaRef.current) {
      textAreaRef.current.focus();
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (scannedReturnedID) {
        try {
          const response = await fetchUserRecord(scannedReturnedID);
          setScannedUserRecord(response);
          setCheckInDisabled(!response?.hardware_info?.pendingReturn);
        } catch (error) {
          console.error('Error fetching user record:', error);
        }
      }
    };
    fetchData();
  }, [scannedReturnedID]);

  const handleCheckOut = () => {
    const processedItems = processHardwareList();
    console.log('Checking out items:', processedItems);
    setActionSuccess({
      type: 'checkout',
      message: JSON.stringify(processedItems, null, 2),
    });
  };

  const handleCheckIn = () => {
    const processedItems = processHardwareList();
    console.log('Checking in items:', processedItems);
    setActionSuccess({
      type: 'checkin',
      message: JSON.stringify(processedItems, null, 2),
    });
  };

  const Card = ({ title, children, className }) => (
    <div className={`bg-white rounded-lg shadow p-6 ${className}`}>
      {title && <h2 className='font-semibold mb-4 text-xl'>{title}</h2>}
      {children}
    </div>
  );

  const InfoItem = ({ icon: Icon, label, value }) => (
    <div className='flex items-center mb-4 space-x-3'>
      <div className='bg-gray-100 flex h-8 items-center justify-center rounded-full w-8'>
        <Icon className='h-4 text-gray-600 w-4' />
      </div>
      <div>
        <p className='text-gray-500 text-sm'>{label}</p>
        <p className='font-medium'>{value || '...'}</p>
      </div>
    </div>
  );

  const TabButton = ({ active, children, onClick }) => (
    <button
      onClick={onClick}
      className={`px-6 py-3 font-medium transition-colors border-b-2 ${
        active
          ? 'border-tree-green text-tree-green'
          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
      }`}>
      {children}
    </button>
  );

  const HardwareGrid = () => (
    <div className='gap-4 grid grid-cols-2 lg:grid-cols-3 mb-6 md:grid-cols-3'>
      {hardwareList.map((item) => (
        <div
          key={item.name}
          className={`flex flex-col space-y-2 p-4 border rounded-lg ${
            item.highValue === 'yes' ? 'bg-yellow-50' : ''
          }`}>
          <div className='flex items-center space-x-2'>
            <input
              type='checkbox'
              checked={selectedItems[item.name] || false}
              onChange={() => handleCheckboxChange(item.name)}
              className='h-4 text-tree-green w-4'
            />
            <label className='flex-1 text-sm'>{item.name}</label>
          </div>
          {selectedItems[item.name] && (
            <input
              type='number'
              min='1'
              max={item.quantity}
              value={itemQuantities[item.name] || ''}
              onChange={(e) => handleQuantityChange(item.name, e.target.value)}
              className='border px-2 py-1 rounded w-full'
              placeholder='Quantity'
            />
          )}
        </div>
      ))}
    </div>
  );

  const CheckoutTab = () => (
    <div className='space-y-6'>
      <div className='flex justify-end'>
        <div className='flex items-center space-x-3'>
          <button
            onClick={() => {
              setScannedUserRecord(null);
              setScannedReturnedID(null);
              setSelectedItems({});
              setItemQuantities({});
              setOtherItems('');
              setActionSuccess(null);
            }}
            className='border border-gray-300 hover:bg-gray-50 inline-flex items-center px-4 py-2 rounded-lg text-gray-700 transition-colors'>
            Clear
          </button>
          <ScanBadgeButton
            scannedReturnedID={scannedReturnedID}
            setScannedReturnedID={setScannedReturnedID}
          />
        </div>
      </div>

      {!scannedReturnedID && (
        <div className='flex h-64 items-center justify-center'>
          <p className='font-medium text-2xl text-gray-700'>
            Please scan a badge to continue...
          </p>
        </div>
      )}

      {scannedUserRecord && (
        <div className='flex flex-col gap-6 md:flex-row'>
          <Card
            title='Participant Info'
            className='md:w-1/4 w-full'>
            <InfoItem
              icon={User}
              label='Full Name'
              value={scannedUserRecord?.application_info?.full_name}
            />
            <InfoItem
              icon={Phone}
              label='Phone Number'
              value={scannedUserRecord?.application_info?.phone}
            />
            <InfoItem
              icon={CalendarDays}
              label='Date of Birth'
              value={formatDate(scannedUserRecord?.application_info?.dob)}
            />
            <InfoItem
              icon={CalendarDays}
              label='Age'
              value={
                scannedUserRecord?.application_info?.dob
                  ? `${calculateAge(
                      scannedUserRecord?.application_info?.dob
                    )} years`
                  : '...'
              }
            />
            <InfoItem
              icon={Users}
              label='Gender'
              value={scannedUserRecord?.application_info?.gender}
            />
          </Card>

          <Card
            title='Hardware Management'
            className='md:w-3/4 w-full'>
            {actionSuccess ? (
              <div className='bg-green-100 p-4 rounded-lg'>
                <p className='font-medium mb-2 text-green-800'>
                  Successfully{' '}
                  {actionSuccess.type === 'checkout'
                    ? 'checked out'
                    : 'returned'}
                  :
                </p>
                <p className='text-green-700 whitespace-pre-wrap'>
                  {actionSuccess.message}
                </p>
              </div>
            ) : (
              <>
                <HardwareGrid />
                <div className='mb-4'>
                  <label className='block font-medium mb-2 text-sm'>
                    Other Items
                  </label>
                  <textarea
                    ref={textAreaRef}
                    value={otherItems}
                    onChange={handleTextAreaChange}
                    placeholder='Format: Item, quantity (one per line)'
                    className='border h-24 p-2 rounded-lg w-full'
                  />
                </div>
                <div className='flex space-x-4'>
                  <button
                    disabled={
                      !Object.keys(selectedItems).length && !otherItems.trim()
                    }
                    onClick={handleCheckOut}
                    className={`flex-1 px-4 py-2 rounded-lg text-white transition-colors ${
                      !Object.keys(selectedItems).length && !otherItems.trim()
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-tree-green hover:bg-tree-green/90'
                    }`}>
                    Check Out
                  </button>
                  <button
                    disabled={checkInDisabled}
                    onClick={handleCheckIn}
                    className={`flex-1 px-4 py-2 rounded-lg text-white transition-colors ${
                      checkInDisabled
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-tree-green hover:bg-tree-green/90'
                    }`}>
                    Return
                  </button>
                </div>
              </>
            )}
          </Card>
        </div>
      )}
    </div>
  );

  const ReturnsPendingTab = () => {
    const pendingReturns = getUsersWithPendingReturns();

    return (
      <div className='space-y-6'>
        <div className='bg-white overflow-hidden rounded-lg shadow'>
          <table className='divide-gray-200 divide-y min-w-full'>
            <thead className='bg-gray-50'>
              <tr>
                <th className='font-medium px-6 py-3 text-gray-500 text-left text-xs tracking-wider uppercase'>
                  Full Name
                </th>
                <th className='font-medium px-6 py-3 text-gray-500 text-left text-xs tracking-wider uppercase'>
                  Hardware List
                </th>
                <th className='font-medium px-6 py-3 text-gray-500 text-left text-xs tracking-wider uppercase'>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className='bg-white divide-gray-200 divide-y'>
              {pendingReturns.map((user, index) => (
                <React.Fragment key={index}>
                  <tr className='hover:bg-gray-50'>
                    <td className='px-6 py-4 whitespace-nowrap'>
                      {user.application_info.full_name}
                    </td>
                    <td className='px-6 py-4'>
                      {user.hardware_info.hardwareList}
                    </td>
                    <td className='px-6 py-4 whitespace-nowrap'>
                      <button
                        onClick={() =>
                          setExpandedRow(expandedRow === index ? null : index)
                        }
                        className='hover:text-tree-green/90 text-tree-green'>
                        {expandedRow === index ? (
                          <ChevronUp />
                        ) : (
                          <ChevronDown />
                        )}
                      </button>
                    </td>
                  </tr>
                  {expandedRow === index && (
                    <tr>
                      <td
                        colSpan='3'
                        className='bg-gray-50 px-6 py-4'>
                        <Card>
                          <div className='gap-6 grid grid-cols-2'>
                            <div className='space-y-4'>
                              <div>
                                <h3 className='font-medium mb-2 text-gray-900'>
                                  Hacker Information
                                </h3>
                                <div className='space-y-2 text-gray-600'>
                                  <p>
                                    <span className='text-gray-500'>
                                      Phone:
                                    </span>{' '}
                                    {user.application_info.phone}
                                  </p>
                                  <p>
                                    <span className='text-gray-500'>
                                      Gender:
                                    </span>{' '}
                                    {user.application_info.gender}
                                  </p>
                                  <p>
                                    <span className='text-gray-500'>Age:</span>{' '}
                                    {calculateAge(user.application_info.dob)}{' '}
                                    years
                                  </p>
                                </div>
                              </div>
                              <button
                                onClick={() => {
                                  /* Add remind functionality */
                                }}
                                className='bg-tree-green hover:bg-tree-green/90 px-4 py-2 rounded-lg text-white transition-colors'>
                                Remind to Return
                              </button>
                            </div>
                            <div>
                              <h3 className='font-medium mb-2 text-gray-900'>
                                Hardware Details
                              </h3>
                              <p className='text-gray-600'>
                                {user.hardware_info.hardwareList}
                              </p>
                            </div>
                          </div>
                        </Card>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Placeholder function for getting users with pending returns
  const getUsersWithPendingReturns = () => {
    // This would be replaced with actual data fetching logic
    return [
      {
        application_info: {
          full_name: 'Sample User',
          phone: '123-456-7890',
          gender: 'F',
          dob: '2000-01-01',
        },
        hardware_info: {
          hardwareList: 'Arduino Kit, Raspberry Pi',
          pendingReturn: true,
        },
      },
    ];
  };

  return (
    <div className='max-w-7xl mx-auto p-6 space-y-6'>
      <LiveClock />
      <div className='mb-6'>
        <h1 className='font-bold mb-6 text-3xl'>Hardware Management</h1>
        <div className='border-b border-gray-200'>
          <TabButton
            active={activeTab === 'checkout'}
            onClick={() => setActiveTab('checkout')}>
            Check Out/In
          </TabButton>
          <TabButton
            active={activeTab === 'pending'}
            onClick={() => setActiveTab('pending')}>
            Returns Pending
          </TabButton>
        </div>
      </div>

      {activeTab === 'checkout' ? <CheckoutTab /> : <ReturnsPendingTab />}
    </div>
  );
};

export default HardwarePage;
